/**
 * mwApp.settings module <br>
 * Work with settings
 *
 * @namespace mwApp/settings
 */

/**
 * ConfigRoutes
 * Configurates module routes
 *
 * @class ConfigRoutes
 * @memberof mwApp/settings
 * @see mwApp/settings
 *
 * @ngdoc config
 * @name mwApp/settings.config:ConfigRoutes
 */
/* @ngInject */
export default function ConfigRoutes ($stateProvider) {
  $stateProvider

  /**
   * abstract state for settings
   */
    .state('app.settings', {
      abstract: true,
      url: '',
      template: '<ui-view/>'
    })

    /**
     * abstract state for /extensions
     */
    .state('app.settings.extensions', {
      abstract: true,
      url: '/extensions',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/settings.controller:ExtensionsCtrl}
     * @name /extensions
     * @memberof mwApp/settings.config:ConfigRoutes
     */
    .state('app.settings.extensions.list', {
      url: '',
      templateUrl: 'components/settings/views/extensions.html',
      controller: 'ExtensionsCtrl as vm',
      label: 'Extensions',
      access: 'viewExtension'
    })

    /**
     * controller {@link mwApp/settings.controller:GeneralSettingsCtrl}
     * @name /generalsettings
     * @memberof mwApp/settings.config:ConfigRoutes
     */
    .state('app.settings.generalsettings', {
      url: '/generalsettings',
      templateUrl: 'components/settings/views/generalSettings.html',
      controller: 'GeneralSettingsCtrl as vm',
      resolve: {
        mailSettings: /* @ngInject */function ($http, alertLiteSvc) {
          const urlEmailSettings = '/nora/api/settings/mail'

          return $http.get(urlEmailSettings)
            .data()
            .then((resp) => resp.payload)
            .catch(rejection => alertLiteSvc.alertError(rejection?.statusText))
        },
        smsServiceSettings: /* @ngInject */function ($http, alertLiteSvc) {
          const url = '/nora/api/settings/sms/trial'

          return $http.get(url)
            .data()
            .then((resp) => resp.payload)
            .catch(rejection => alertLiteSvc.alertError(rejection?.statusText))
        }
      },
      label: 'General Settings',
      access: 'viewGlobalGeneral'
    })

    /**
     * abstract state for /systemusers
     */
    .state('app.systemusers', {
      abstract: true,
      url: '/systemusers',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/settings.controller:SystemUsersCtrl}
     * @name /systemusers
     * @memberof mwApp/settings.config:ConfigRoutes
     */
    .state('app.systemusers.list', {
      url: '',
      templateUrl: 'components/settings/views/systemUsers.html',
      controller: 'SystemUsersCtrl as vm',
      label: 'Users',
      access: 'viewUser'
    })

    /**
     * abstract state for /systemusers/:userId
     */
    .state('app.systemusers.systemuser', {
      abstract: true,
      url: '/:userId',
      template: '<ui-view/>',
      resolve: {
        user: /* @ngInject */ function ($transition$, User) {
          var { userId: id } = $transition$.params()
          return +id
            ? User.load({ id })
            : new User()
        }
      }
    })

    /**
     * controller {@link mwApp/settings.controller:AddSystemUserCtrl}
     * @name /systemusers/:userId
     * @memberof mwApp/settings.config:ConfigRoutes
     */
    .state('app.systemusers.systemuser.edit', {
      url: '',
      templateUrl: 'components/settings/views/addSystemUser.html',
      controller: 'AddSystemUserCtrl as vm',
      label: scope => (scope.userId === 'rules' ? null // skip this route if it is inside of rules
        : (scope.userId && +scope.userId ? 'Edit' : 'Add') + ' User'),
      access: ':userId (+) ! IF createUser ELSE editUser'
    })

    /**
     * abstract state for /systemusers/rules/:ruleId
     */
    .state('app.systemusers.rule', {
      abstract: true,
      url: '/rules/:ruleId',
      template: '<ui-view/>',
      resolve: {
        rule: /* @ngInject */ function ($transition$, $state, Rule, authSvc) {
          var { ruleId: id } = $transition$.params()
          const me = authSvc.getUser()
          if (me.rule.id === +id) {
            return $state.go('app.403')
          }
          return +id
            ? Rule.load({ id }).then().catch(err => {
              if (err.status === 403) {
                $state.go('app.403')
              }
            })
            : new Rule()
        }
      }
    })

    /**
     * controller {@link mwApp/settings.controller:AddPermissionRuleCtrl}
     * @name /systemusers/rules/:ruleId
     * @memberof mwApp/settings.config:ConfigRoutes
     */
    .state('app.systemusers.rule.edit', {
      url: '',
      templateUrl: 'components/settings/views/addPermissionRule.html',
      controller: 'AddPermissionRuleCtrl as vm',
      label: '{{::ruleId | addOrEdit}} Permission Rule',
      access: ':ruleId (+) ! IF createUser ELSE editUser'
    })

    /**
     * abstract state for /paymentservices
     */
    .state('app.settings.paymentservices', {
      abstract: true,
      url: '/paymentservices',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/settings.controller:PaymentSystemCtrl}
     * @name /paymentservices
     * @memberof mwApp/settings.config:ConfigRoutes
     */
    .state('app.settings.paymentservices.list', {
      url: '',
      templateUrl: 'components/settings/views/paymentSystem.html',
      controller: 'PaymentSystemCtrl as vm',
      label: 'Payment Services',
      access: 'viewGlobalPayment viewCurrency |'
    })

    /**
     * abstract state for /apps-settings
     */
    .state('app.settings.appsSettings', {
      abstract: true,
      url: '/apps-settings',
      template: '<ui-view/>',
      resolve: {
        productTour: /* @ngInject */ function ($state, ProductTour) {
          // if (!window.ENV.APP_PRODUCT_TOUR_ENABLED) {
          //   return $state.go('app.404')
          // }
          return ProductTour.loadPublic()
        }
      }

    })

    /**
     * controller {@link mwApp/settings.controller:appsSettingsCtrl}
     * @name /applications
     * @memberof mwApp/settings.config:ConfigRoutes
     */
    .state('app.settings.appsSettings.list', {
      url: '',
      views: {
        '': {
          templateUrl: 'components/settings/views/appsSettings.html',
          controller: 'AppsSettingsCtrl as vm'
        },
        'productTour@app.settings.appsSettings.list': {
          templateUrl: 'components/settings/views/productTour.html',
          controller: 'ProductTourCtrl as vm'
        }
      },
      label: 'Apps Settings',
      access: 'viewAppsSettings'
    })

    /**
     * abstract state for /extusers
     */
    .state('app.extusers', {
      abstract: true,
      url: '/extusers',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/settings.controller:ExtUsersCtrl}
     * @name /extusers
     * @memberof mwApp/settings.config:ConfigRoutes
     */
    .state('app.extusers.list', {
      url: '',
      templateUrl: 'components/settings/views/externalUsers.html',
      controller: 'ExternalUsersCtrl as vm',
      label: 'API User',
      access: 'viewAPIAccessGlobal'
    })

    /**
     * abstract state for /extusers/:extUserId
     */
    .state('app.extusers.extuser', {
      abstract: true,
      url: '/:extUserId',
      template: '<ui-view/>',
      resolve: {
        user: /* @ngInject */ function ($transition$, ExternalUser) {
          var { extUserId: id } = $transition$.params()
          return +id
            ? ExternalUser.load({ id })
            : new ExternalUser()
        }
      }
    })

    /**
     * controller {@link mwApp/settings.controller:AddExternalUserCtrl}
     * @name /extusers/:extUserId
     * @memberof mwApp/settings.config:ConfigRoutes
     */
    .state('app.extusers.extuser.edit', {
      url: '',
      templateUrl: 'components/settings/views/addExternalUser.html',
      controller: 'AddExternalUserCtrl as vm',
      label: '{{::extUserId | addOrEdit}} API User',
      access: 'viewAPIAccessGlobal'
    })
}
